package com.havn.app.core.media

import com.havn.app.core.supabase.HavnSupabaseConfig
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

object HavnMediaBucket {
    const val PropertyPhotos = "property-photos"
    const val ContactPhotos = "contact-photos"
    const val ContactPasses = "contact-passes"
    const val AgentPhotos = "agent-photos"
    const val AgencyMedia = "agency-media"
}

fun havnPublicStorageUrl(bucket: String, storagePath: String): String =
    "${HavnSupabaseConfig.url}/storage/v1/object/public/$bucket/${storagePath.encodeStoragePathForUrl()}"

fun havnPublicStorageSource(bucket: String, source: String?): String? {
    val path = source.normalizedStoragePath(bucket) ?: return source?.takeIf { it.isNotBlank() }
    return havnPublicStorageUrl(bucket, path)
}

fun havnRenderedStorageSource(
    bucket: String,
    source: String?,
    width: Int,
    quality: Int,
    fallbackPath: String? = null
): String? {
    @Suppress("UNUSED_VARIABLE")
    val ignoredWidth = width
    @Suppress("UNUSED_VARIABLE")
    val ignoredQuality = quality
    val path = source.normalizedStoragePath(bucket) ?: fallbackPath.normalizedStoragePath(bucket) ?: return source?.takeIf { it.isNotBlank() }
    return havnPublicStorageUrl(bucket, path)
}

fun havnPreviewStorageSource(
    bucket: String,
    source: String?,
    width: Int,
    quality: Int = 62
): String? =
    havnRenderedStorageSource(
        bucket = bucket,
        source = source,
        width = width.coerceIn(48, 480),
        quality = quality.coerceIn(45, 78)
    )

fun havnPropertyPhotoPreviewSource(source: String?, width: Int = 220): String? =
    havnPreviewStorageSource(HavnMediaBucket.PropertyPhotos, source, width)

fun havnContactAvatarPreviewSource(source: String?, width: Int = 96): String? =
    havnPreviewStorageSource(HavnMediaBucket.ContactPhotos, source, width)

fun havnAgentAvatarPreviewSource(source: String?, width: Int = 96): String? =
    havnPreviewStorageSource(HavnMediaBucket.AgentPhotos, source, width)

fun havnAgencyMediaPreviewSource(source: String?, width: Int = 96): String? =
    havnPreviewStorageSource(HavnMediaBucket.AgencyMedia, source, width)

fun String?.normalizedStoragePath(bucket: String): String? {
    val value = this?.trim()?.takeIf { it.isNotBlank() } ?: return null
    if (value.startsWith("data:", ignoreCase = true)) return null
    val objectMarker = "/storage/v1/object/public/$bucket/"
    val renderMarker = "/storage/v1/render/image/public/$bucket/"
    val marker = when {
        objectMarker in value -> objectMarker
        renderMarker in value -> renderMarker
        value.startsWith("http://", ignoreCase = true) || value.startsWith("https://", ignoreCase = true) -> return null
        value.startsWith("$bucket/") -> return value.removePrefix("$bucket/").substringBefore('?')
        else -> return value.substringBefore('?')
    }
    return value.substringAfter(marker).substringBefore('?').takeIf { it.isNotBlank() }
}

@OptIn(ExperimentalEncodingApi::class)
fun String.decodeHavnDataUrlBytes(): ByteArray? {
    val commaIndex = indexOf(',')
    if (commaIndex < 0 || !substring(0, commaIndex).contains(";base64", ignoreCase = true)) return null
    val encoded = substring(commaIndex + 1).takeIf { it.isNotBlank() } ?: return null
    return runCatching { Base64.decode(encoded) }.getOrNull()
}

fun String.havnDataUrlContentType(default: String = "image/jpeg"): String =
    substringBefore(',', missingDelimiterValue = "")
        .removePrefix("data:")
        .substringBefore(';')
        .takeIf { it.startsWith("image/") }
        ?: default

fun String.havnFileExtension(): String = when (substringAfter('/', missingDelimiterValue = "").lowercase()) {
    "jpeg", "jpg" -> "jpg"
    "png" -> "png"
    "webp" -> "webp"
    "heic" -> "heic"
    "avif" -> "avif"
    else -> "jpg"
}

fun String.encodeStoragePathForUrl(): String =
    split('/').joinToString("/") { segment ->
        buildString {
            segment.forEach { char ->
                when {
                    char in 'a'..'z' ||
                        char in 'A'..'Z' ||
                        char in '0'..'9' ||
                        char == '-' ||
                        char == '_' ||
                        char == '.' ||
                        char == '~' -> append(char)
                    char == ' ' -> append("%20")
                    else -> append('%').append(char.code.toString(16).uppercase().padStart(2, '0'))
                }
            }
        }
    }
